import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, MessageCircle, Mail, Share2, FileDown, Printer, Search, UserPlus, ArrowRight } from "lucide-react";
import { SectionShell } from "./ui";
import { API, authHeaders, formatApiErrorDetail } from "./api";
import { ScenarioBar } from "./ScenarioBar";
import {
  cash,
  tax,
  ni,
  payment,
  sdlt,
  BUYER_OPTIONS,
  FEE_ITEMS,
  DEFAULTS,
  buyerHelp,
} from "../pages/calculator/calc";
import { Panel, Field, Input, Select, Checkbox, Card, Row } from "../pages/calculator/parts";
import { ClientDetails, emptyApplicant } from "../pages/calculator/ClientDetails";
import {
  buildScenarioSummary,
  exportScenarioPdf,
  printScenario,
} from "../pages/calculator/scenarioPdf";

const CustomerGate = ({ customers, onPicked, onCustomerCreated }) => {
  const [mode, setMode] = useState("existing");
  const [search, setSearch] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [newCust, setNewCust] = useState({ name: "", company: "", email: "" });
  const [working, setWorking] = useState(false);

  const filtered = (customers || [])
    .filter((c) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        (c.name || "").toLowerCase().includes(q) ||
        (c.company || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q)
      );
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const confirm = async () => {
    setWorking(true);
    try {
      if (mode === "new") {
        const name = newCust.name.trim();
        if (!name) {
          toast.error("Give the customer a name.");
          setWorking(false);
          return;
        }
        const { data: c } = await axios.post(`${API}/admin/customers`, {
          name,
          company: newCust.company || "",
          email: newCust.email || "",
          address: "",
        }, authHeaders());
        onCustomerCreated?.();
        onPicked(c.id);
      } else {
        if (!customerId) {
          toast.error("Pick a customer to link this scenario to.");
          setWorking(false);
          return;
        }
        onPicked(customerId);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create customer");
    } finally {
      setWorking(false);
    }
  };

  return (
    <div data-testid="calc-customer-gate" className="bg-white border border-forest/10 rounded-xl p-6 sm:p-8 max-w-2xl space-y-5">
      <div>
        <p className="text-[10px] tracking-[0.24em] uppercase text-gold font-bold">Step 1 of 2</p>
        <h3 className="font-serif text-xl text-forest mt-1">Who is this scenario for?</h3>
        <p className="text-[12px] text-sage mt-2">Each saved scenario is filed under one customer so you can reopen it later from their page.</p>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setMode("existing")}
          data-testid="calc-gate-mode-existing"
          className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 border rounded-lg text-[12px] font-bold tracking-[0.14em] uppercase transition-colors ${
            mode === "existing" ? "bg-forest text-cream border-forest" : "bg-white text-forest border-forest/15 hover:border-forest/40"
          }`}
        ><Search size={14} /> Existing customer</button>
        <button
          type="button"
          onClick={() => setMode("new")}
          data-testid="calc-gate-mode-new"
          className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 border rounded-lg text-[12px] font-bold tracking-[0.14em] uppercase transition-colors ${
            mode === "new" ? "bg-forest text-cream border-forest" : "bg-white text-forest border-forest/15 hover:border-forest/40"
          }`}
        ><UserPlus size={14} /> New customer</button>
      </div>

      {mode === "existing" ? (
        <div className="space-y-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, company or email…"
            data-testid="calc-gate-search"
            className="w-full bg-cream-3 border border-forest/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none"
          />
          <div className="border border-forest/10 rounded-lg max-h-72 overflow-y-auto divide-y divide-forest/5" data-testid="calc-gate-list">
            {filtered.length === 0 && <p className="px-4 py-6 text-sm text-sage text-center">No matches. Try "New customer".</p>}
            {filtered.map((c) => (
              <button
                type="button"
                key={c.id}
                onClick={() => setCustomerId(c.id)}
                data-testid={`calc-gate-customer-${c.id}`}
                className={`w-full text-left px-4 py-3 flex items-center justify-between transition-colors ${
                  customerId === c.id ? "bg-gold-soft/20" : "hover:bg-cream-3"
                }`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-forest truncate">{c.name}</p>
                  <p className="text-[11px] text-sage mt-0.5 truncate">{[c.company, c.email].filter(Boolean).join(" · ") || "—"}</p>
                </div>
                {customerId === c.id && <span className="text-[10px] tracking-[0.14em] uppercase font-bold text-gold-dark">Selected</span>}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <input type="text" value={newCust.name} onChange={(e) => setNewCust((n) => ({ ...n, name: e.target.value }))} placeholder="Full name" data-testid="calc-gate-newcust-name" className="w-full bg-cream-3 border border-forest/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input type="text" value={newCust.company} onChange={(e) => setNewCust((n) => ({ ...n, company: e.target.value }))} placeholder="Company (optional)" data-testid="calc-gate-newcust-company" className="bg-cream-3 border border-forest/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
            <input type="email" value={newCust.email} onChange={(e) => setNewCust((n) => ({ ...n, email: e.target.value }))} placeholder="Email (optional)" data-testid="calc-gate-newcust-email" className="bg-cream-3 border border-forest/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
          </div>
        </div>
      )}

      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={confirm}
          disabled={working || (mode === "existing" ? !customerId : !newCust.name.trim())}
          data-testid="calc-gate-continue"
          className="btn-gold disabled:opacity-50 inline-flex items-center gap-2"
        >
          Open calculator <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
};


export const CalculatorSection = () => {
  const [state, setState] = useState(() => ({
    applicationType: DEFAULTS.applicationType,
    applicant1: emptyApplicant(),
    applicant2: emptyApplicant(),
    incomeMode: DEFAULTS.incomeMode,
    incomeSplit: DEFAULTS.incomeSplit,
    price: DEFAULTS.price,
    deposit: DEFAULTS.deposit,
    rate: DEFAULTS.rate,
    term: DEFAULTS.term,
    multiple: DEFAULTS.multiple,
    income1: DEFAULTS.income1,
    income2: DEFAULTS.income2,
    deductions: DEFAULTS.deductions,
    costs: DEFAULTS.costs,
    buyer: DEFAULTS.buyer,
    eligible: DEFAULTS.eligible,
    nonresident: DEFAULTS.nonresident,
    fees: FEE_ITEMS.reduce(
      (acc, f) => ({ ...acc, [f.key]: { on: false, amount: "" } }),
      {}
    ),
    payeMonthly: "",
    payeManual: false, // once admin edits the PAYE field themselves we stop auto-filling
  }));

  const [customers, setCustomers] = useState([]);
  const [loadedScenario, setLoadedScenario] = useState(null);
  const [activeCustomerId, setActiveCustomerId] = useState(null);

  const loadCustomers = () =>
    axios
      .get(`${API}/admin/customers`, authHeaders())
      .then(({ data }) => setCustomers(data || []))
      .catch(() => setCustomers([]));

  useEffect(() => {
    loadCustomers();
  }, []);

  const joint = state.applicationType === "joint";

  // Auto-income — recompute suggested salaries from loan + income multiple.
  useEffect(() => {
    if (state.incomeMode !== "auto") return;
    const { price, deposit, multiple, incomeSplit } = state;
    if (!(price > 0 && deposit >= 0 && deposit < 100 && multiple > 0)) return;
    const total = Math.ceil((price * (1 - deposit / 100)) / multiple * 100) / 100;
    const split = joint ? Number(incomeSplit) : 100;
    const a1 = Math.round(total * split) / 100;
    setState((s) => ({
      ...s,
      income1: Number(a1.toFixed(2)),
      income2: Number((joint ? total - a1 : 0).toFixed(2)),
    }));
  }, [state.incomeMode, state.price, state.deposit, state.multiple, state.incomeSplit, joint]);

  const results = useMemo(() => {
    const v = {
      price: Number(state.price) || 0,
      deposit: Number(state.deposit) || 0,
      rate: Number(state.rate) || 0,
      term: Number(state.term) || 0,
      multiple: Number(state.multiple) || 0,
      income1: Number(state.income1) || 0,
      income2: joint ? Number(state.income2) || 0 : 0,
      deductions: Number(state.deductions) || 0,
      costs: Number(state.costs) || 0,
    };
    const loan = v.price * (1 - v.deposit / 100);
    const r = v.rate / 100;
    const rep = loan > 0 && v.term > 0 ? payment(loan, r, v.term) : 0;
    const io = loan * r / 12;
    const g = v.income1 + v.income2;
    const t1 = tax(v.income1) / 12;
    const t2 = tax(v.income2) / 12;
    const n1 = ni(v.income1);
    const n2 = ni(v.income2);
    const net = g / 12 - t1 - t2 - n1 - n2 - v.deductions;
    const duty = sdlt(v.price, state.buyer, state.nonresident, state.eligible);
    const selectedFees = FEE_ITEMS.filter((f) => state.fees[f.key]?.on);
    const feeTotal = selectedFees.reduce(
      (sum, f) =>
        sum +
        (f.percent
          ? (loan * (Number(state.fees[f.key].amount) || 0)) / 100
          : Number(state.fees[f.key].amount) || 0),
      0
    );
    const cashTotal = (v.price * v.deposit) / 100 + duty + feeTotal;
    return {
      v,
      loan,
      r,
      rep,
      io,
      g,
      t1,
      t2,
      n1,
      n2,
      net,
      duty,
      selectedFees,
      feeTotal,
      cashTotal,
    };
  }, [state, joint]);

  // Required-income → auto-fill the PAYE monthly gross box with the illustrative
  // required income divided by 12, so the admin sees the net take-home for that
  // income straight away. We only auto-fill while the admin hasn't typed into
  // the PAYE box themselves.
  const requiredIncomeYear = results.loan / Math.max(results.v.multiple, 0.0001);
  const requiredMonthly = requiredIncomeYear / 12;
  useEffect(() => {
    if (state.payeManual) return;
    const next = requiredMonthly > 0 ? requiredMonthly.toFixed(2) : "";
    setState((s) =>
      s.payeMonthly === next ? s : { ...s, payeMonthly: next }
    );
  }, [requiredMonthly, state.payeManual]);

  const buyerLabel =
    BUYER_OPTIONS.find((b) => b.value === state.buyer)?.label || "";

  const summary = useMemo(
    () => buildScenarioSummary(state, results, buyerLabel, joint),
    [state, results, buyerLabel, joint]
  );

  const setField = (field) => (e) => {
    const value =
      e && e.target
        ? e.target.type === "checkbox"
          ? e.target.checked
          : e.target.value
        : e;
    setState((s) => ({ ...s, [field]: value }));
  };

  const setFee = (key, patch) =>
    setState((s) => ({
      ...s,
      fees: { ...s.fees, [key]: { ...s.fees[key], ...patch } },
    }));

  const setExamplePrice = (p) =>
    setState((s) => ({ ...s, price: p, deposit: 15, multiple: 5 }));

  // PAYE results — driven by whichever gross monthly value is currently in the box
  // (auto-filled from required income or admin-overridden).
  const paye = useMemo(() => {
    const m = Number(state.payeMonthly);
    if (!(m > 0)) return null;
    const annual = m * 12;
    const itYear = tax(annual);
    const itM = itYear / 12;
    const niM = ni(annual);
    const ded = itM + niM;
    const net = m - ded;
    return { m, annual, itYear, itM, niM, ded, net };
  }, [state.payeMonthly]);

  // Monthly net from the required-income calculation (for the summary chip next
  // to the Required-income card). Uses the same tax/NI functions.
  const requiredNetMonthly = useMemo(() => {
    if (!(requiredMonthly > 0)) return 0;
    return (
      requiredMonthly - tax(requiredMonthly * 12) / 12 - ni(requiredMonthly * 12)
    );
  }, [requiredMonthly]);

  const copyScenario = async () => {
    try {
      await navigator.clipboard.writeText(summary);
      toast.success("Scenario copied.");
    } catch {
      toast.error("Clipboard blocked — select the text in the box below instead.");
    }
  };
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(summary)}`;
  const emailHref = `mailto:?subject=${encodeURIComponent("Elin's Consultancy – Mortgage Scenario")}&body=${encodeURIComponent(summary)}`;
  const nativeShare = async () => {
    if (!navigator.share) {
      toast.message("Share sheet not available here. Use Copy scenario or the app links.");
      return;
    }
    try {
      await navigator.share({
        title: "Elin's Consultancy – Mortgage Scenario",
        text: summary,
      });
    } catch (e) {
      if (e.name !== "AbortError") toast.error("Sharing unavailable here.");
    }
  };

  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      await exportScenarioPdf(state, results, buyerLabel, joint);
    } catch (e) {
      console.error(e);
      toast.error("PDF generation failed. Try Print / Save PDF instead.");
    } finally {
      setPdfBusy(false);
    }
  };

  const resetAll = () => {
    setState({
      applicationType: DEFAULTS.applicationType,
      applicant1: emptyApplicant(),
      applicant2: emptyApplicant(),
      incomeMode: DEFAULTS.incomeMode,
      incomeSplit: DEFAULTS.incomeSplit,
      price: DEFAULTS.price,
      deposit: DEFAULTS.deposit,
      rate: DEFAULTS.rate,
      term: DEFAULTS.term,
      multiple: DEFAULTS.multiple,
      income1: DEFAULTS.income1,
      income2: "",
      deductions: "",
      costs: "",
      buyer: DEFAULTS.buyer,
      eligible: false,
      nonresident: false,
      fees: FEE_ITEMS.reduce(
        (acc, f) => ({ ...acc, [f.key]: { on: false, amount: "" } }),
        {}
      ),
      payeMonthly: "",
      payeManual: false,
    });
    setLoadedScenario(null);
  };

  const activeCustomer = useMemo(
    () => customers.find((c) => c.id === activeCustomerId) || null,
    [customers, activeCustomerId]
  );

  // When a scenario is loaded from the ScenarioBar it may belong to a
  // different customer — sync the active customer to it so the top banner
  // stays accurate.
  useEffect(() => {
    if (loadedScenario?.customer_id && loadedScenario.customer_id !== activeCustomerId) {
      setActiveCustomerId(loadedScenario.customer_id);
    }
  }, [loadedScenario, activeCustomerId]);

  if (!activeCustomerId) {
    return (
      <SectionShell
        title="Mortgage Scenario Calculator"
        subtitle="Internal illustration tool — link a customer before building their scenario."
        testid="admin-calculator-section"
      >
        <CustomerGate
          customers={customers}
          onPicked={(id) => setActiveCustomerId(id)}
          onCustomerCreated={() => loadCustomers()}
        />
      </SectionShell>
    );
  }

  return (
    <SectionShell
      title="Mortgage Scenario Calculator"
      subtitle="Internal illustration tool — build a mortgage scenario, view PAYE take-home against the required income, calculate SDLT and share with the client. Not visible on the public website."
      testid="admin-calculator-section"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap bg-cream-3 border border-gold/30 rounded-xl px-4 py-3" data-testid="calc-active-customer-banner">
        <div className="min-w-0">
          <p className="text-[10px] tracking-[0.24em] uppercase text-gold font-bold">Linked customer</p>
          <p className="font-serif text-xl text-forest mt-0.5 truncate">{activeCustomer?.name || "—"}</p>
          {activeCustomer?.company && <p className="text-[12px] text-sage">{activeCustomer.company}</p>}
        </div>
        <button
          type="button"
          onClick={() => { setActiveCustomerId(null); setLoadedScenario(null); }}
          data-testid="calc-switch-customer"
          className="inline-flex items-center text-[11px] font-bold tracking-[0.2em] uppercase text-forest border border-forest/25 hover:border-forest px-4 py-2 rounded transition-colors self-center"
        >
          Switch customer
        </button>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <p className="text-[12px] text-sage max-w-xl leading-relaxed">
          Numbers update as you type. The PAYE field at the bottom pre-fills with
          the required monthly gross so the client's take-home shows instantly.
        </p>
        <button
          type="button"
          onClick={resetAll}
          data-testid="calc-reset"
          className="inline-flex items-center text-[11px] font-bold tracking-[0.2em] uppercase text-forest border border-forest/25 hover:border-forest px-5 py-2.5 transition-colors"
        >
          Reset
        </button>
      </div>

      <ClientDetails state={state} setState={setState} />

      <ScenarioBar
        state={state}
        setState={setState}
        loaded={loadedScenario}
        setLoaded={setLoadedScenario}
        customers={customers}
        fixedCustomerId={activeCustomerId}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.1fr] gap-6">
        <Panel title="Property & borrowing" testid="calc-property-panel">
          <div className="flex gap-3 flex-wrap">
            {[400000, 450000].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setExamplePrice(p)}
                className="text-[12px] font-semibold text-forest border border-forest/20 hover:border-forest px-4 py-2 transition-colors"
              >
                £{p.toLocaleString()} example
              </button>
            ))}
          </div>
          <Field label="Property price (£)">
            <Input type="number" min="0" value={state.price} onChange={setField("price")} testid="calc-price" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Deposit (%)">
              <Input type="number" min="0" max="99" value={state.deposit} onChange={setField("deposit")} testid="calc-deposit" />
            </Field>
            <Field label="Interest rate (%)">
              <Input type="number" step="0.01" min="0" value={state.rate} onChange={setField("rate")} testid="calc-rate" />
            </Field>
            <Field label="Term (years)">
              <Input type="number" min="1" max="40" value={state.term} onChange={setField("term")} testid="calc-term" />
            </Field>
            <Field label="Income multiple">
              <Input type="number" step="0.1" min="0.1" value={state.multiple} onChange={setField("multiple")} testid="calc-multiple" />
            </Field>
          </div>

          <div className="pt-4 border-t border-forest/10 space-y-4">
            <p className="text-[11px] tracking-[0.16em] uppercase font-bold text-forest/70">
              Applicant income
            </p>
            <Field label="Income calculation">
              <Select value={state.incomeMode} onChange={setField("incomeMode")} testid="calc-income-mode">
                <option value="auto">Calculate required income automatically</option>
                <option value="actual">Enter actual applicant income</option>
              </Select>
            </Field>
            {state.incomeMode === "auto" && joint && (
              <Field label="Applicant 1 share of required income (%)">
                <Input type="number" min="0" max="100" value={state.incomeSplit} onChange={setField("incomeSplit")} />
              </Field>
            )}
            <p className="text-[12px] text-sage leading-relaxed">
              {state.incomeMode === "auto"
                ? "Illustrative required gross income, not the client's verified earnings. Taxes and take-home pay use this calculated income."
                : "Taxes and take-home pay use the actual salaries you enter."}
            </p>
            <Field label="Applicant 1 gross annual salary (£)">
              <Input type="number" min="0" value={state.income1} onChange={setField("income1")} readOnly={state.incomeMode === "auto"} testid="calc-income1" />
            </Field>
            {joint && (
              <Field label="Applicant 2 gross annual salary (£)">
                <Input type="number" min="0" value={state.income2} onChange={setField("income2")} readOnly={state.incomeMode === "auto"} testid="calc-income2" />
              </Field>
            )}
            <Field label="Other take-home deductions / month (£)">
              <Input type="number" min="0" placeholder="0" value={state.deductions} onChange={setField("deductions")} />
            </Field>
            <Field label="Living costs & commitments / month (£)">
              <Input type="number" min="0" placeholder="0" value={state.costs} onChange={setField("costs")} />
            </Field>
          </div>
        </Panel>

        <Panel title="Your scenario" testid="calc-results-panel">
          <div className="flex flex-wrap gap-2">
            <a href={whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="calc-share-whatsapp" className="inline-flex items-center gap-2 text-[12px] font-semibold text-forest border border-forest/25 hover:border-forest px-3.5 py-2 transition-colors">
              <MessageCircle size={14} /> Send by WhatsApp
            </a>
            <a href={emailHref} data-testid="calc-share-email" className="inline-flex items-center gap-2 text-[12px] font-semibold text-forest border border-forest/25 hover:border-forest px-3.5 py-2 transition-colors">
              <Mail size={14} /> Send by Email
            </a>
            <button type="button" onClick={nativeShare} data-testid="calc-share-native" className="inline-flex items-center gap-2 text-[12px] font-semibold text-forest border border-forest/25 hover:border-forest px-3.5 py-2 transition-colors">
              <Share2 size={14} /> Share via iPad / phone
            </button>
            <button type="button" onClick={copyScenario} data-testid="calc-share-copy" className="inline-flex items-center gap-2 text-[12px] font-semibold text-forest border border-forest/25 hover:border-forest px-3.5 py-2 transition-colors">
              <Copy size={14} /> Copy scenario
            </button>
          </div>
          <details className="group">
            <summary className="text-[12.5px] font-semibold text-forest cursor-pointer">View / copy message</summary>
            <textarea data-testid="calc-share-text" readOnly value={summary} className="w-full mt-2 bg-cream-3 border border-forest/10 px-3 py-3 text-[12.5px] text-ink/90 h-48 font-mono" />
          </details>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <Card eyebrow="Repayment / month" value={cash(results.rep)} note="Capital + interest" featured testid="calc-card-repayment" />
            <Card eyebrow="Interest-only / month" value={cash(results.io)} note="Capital remains payable" testid="calc-card-io" />
            <Card
              eyebrow="Required gross income / year"
              value={cash(requiredIncomeYear)}
              note={`At ${results.v.multiple}× household income · ${cash(requiredNetMonthly)} net / month`}
              testid="calc-card-required-income"
            />
            <Card eyebrow="Estimated take-home / month" value={cash(results.net)} note="After tax, NI and entered deductions" testid="calc-card-takehome" />
          </div>

          <div className="pt-3 border-t border-forest/8">
            <Row label="Deposit required" value={cash((results.v.price * results.v.deposit) / 100)} />
            <Row label="Mortgage required" value={cash(results.loan)} />
            <Row label="Loan-to-value" value={`${(100 - results.v.deposit).toFixed(1)}%`} />
            <Row label="Income-multiple borrowing margin" value={cash(results.g * results.v.multiple - results.loan)} />
            <Row label="Applicant 1 monthly tax / NI" value={`${cash(results.t1)} / ${cash(results.n1)}`} />
            <Row label="Applicant 2 monthly tax / NI" value={`${cash(results.t2)} / ${cash(results.n2)}`} />
            <Row label="Remaining after repayment & living costs" value={cash(results.net - results.rep - results.v.costs)} />
            <Row label="Remaining after interest-only & living costs" value={cash(results.net - results.io - results.v.costs)} />
            <Row label="Repayment share of take-home" value={results.net > 0 ? `${((results.rep / results.net) * 100).toFixed(1)}%` : "Not applicable"} />
            <Row label="Interest-only capital due at term-end" value={cash(results.loan)} />
          </div>
          <p className="text-[12px] text-ink/80 bg-gold-soft/25 border border-gold/40 px-3 py-2 leading-relaxed">
            Income multiples do not establish lender approval. Interest-only requires lender eligibility and a capital repayment strategy. The interest rate is illustrative.
          </p>

          <div className="pt-2">
            <h3 className="font-serif text-xl text-forest mb-2">Compare repayment terms</h3>
            <div className="overflow-x-auto">
              <table className="min-w-[460px] w-full text-[13px]" data-testid="calc-term-table">
                <thead>
                  <tr className="text-left text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">
                    <th className="py-2 pr-3">Term</th>
                    <th className="py-2 pr-3 text-right">Repayment / month</th>
                    <th className="py-2 text-right">Interest-only / month</th>
                  </tr>
                </thead>
                <tbody>
                  {[20, 25, 30, 35, 40].map((y) => (
                    <tr key={y} className="border-t border-forest/8">
                      <td className="py-2 pr-3">{y} years</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{cash(payment(results.loan, results.r, y))}</td>
                      <td className="py-2 text-right tabular-nums">{cash(results.io)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Panel>
      </div>

      <Panel
        title="PAYE Tax Calculator"
        subtitle="Pre-filled with the required monthly gross from the scenario above. Edit it to see take-home for any salary. Standard UK Income Tax and employee NI only (2026/27, England, Wales & Northern Ireland)."
        testid="calc-paye-panel"
      >
        <Field label="Monthly gross salary (£)">
          <Input
            type="number"
            min="0"
            placeholder="e.g. 3000"
            value={state.payeMonthly}
            onChange={(e) =>
              setState((s) => ({ ...s, payeMonthly: e.target.value, payeManual: true }))
            }
            testid="calc-paye-monthly"
          />
          {state.payeManual && (
            <button
              type="button"
              onClick={() => setState((s) => ({ ...s, payeManual: false }))}
              className="text-[11px] font-bold tracking-[0.14em] uppercase text-forest/70 hover:text-forest transition-colors mt-2"
            >
              Re-link to required income
            </button>
          )}
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
          <Card eyebrow="Gross salary / month" value={cash(paye?.m || 0)} />
          <Card eyebrow="Income Tax / month" value={cash(paye?.itM || 0)} />
          <Card eyebrow="National Insurance / month" value={cash(paye?.niM || 0)} />
          <Card eyebrow="Total deductions / month" value={cash(paye?.ded || 0)} />
          <Card eyebrow="Net take-home / month" value={cash(paye?.net || 0)} featured testid="calc-paye-net" />
          <Card eyebrow="Net take-home / year" value={cash((paye?.net || 0) * 12)} />
        </div>
        {paye && (
          <p className="text-[12px] text-sage leading-relaxed">
            Annualised gross {cash(paye.annual)}. Income Tax {cash(paye.itYear)}/yr and employee NI {cash(paye.niM * 12)}/yr. 2026/27 bands: £12,570 personal allowance (tapered above £100,000), 20% to £37,700, 40% to £125,140, 45% above.
          </p>
        )}
      </Panel>

      <Panel
        title="Stamp Duty & purchase costs"
        subtitle="England and Northern Ireland · Individual buyers · Current residential rates"
        testid="calc-sdlt-panel"
      >
        <Field label="Buyer situation">
          <Select value={state.buyer} onChange={setField("buyer")} testid="calc-buyer">
            {BUYER_OPTIONS.map((b) => (
              <option key={b.value} value={b.value}>
                {b.label}
              </option>
            ))}
          </Select>
        </Field>
        <Checkbox label="2% non-UK resident SDLT surcharge applies" checked={state.nonresident} onChange={setField("nonresident")} testid="calc-nonresident" />
        {state.buyer === "first" && (
          <Checkbox label="Confirm first-time buyer eligibility (own home, never owned before, within price cap)" checked={state.eligible} onChange={setField("eligible")} testid="calc-eligible" />
        )}
        <p className="text-[12px] text-sage leading-relaxed">
          {buyerHelp(state.buyer, state.eligible, Number(state.price) || 0)}
        </p>

        <div className="pt-5 border-t border-forest/10">
          <h3 className="font-serif text-xl text-forest">Optional fees</h3>
          <p className="text-[12px] text-sage mt-1 leading-relaxed">
            Tick each fee you want included and enter the quote, including VAT where applicable. Unselected costs are excluded, not assumed free.
          </p>
          <div className="mt-4 space-y-3">
            {FEE_ITEMS.map((f) => {
              const on = !!state.fees[f.key]?.on;
              const amtRaw = state.fees[f.key]?.amount;
              const amtNum = Number(amtRaw) || 0;
              const computed = f.percent ? (results.loan * amtNum) / 100 : amtNum;
              return (
                <div key={f.key} className="grid grid-cols-1 sm:grid-cols-[1fr_200px] gap-3 items-start sm:items-center bg-cream-3 border border-forest/10 px-4 py-3">
                  <div>
                    <Checkbox label={f.label} checked={on} onChange={() => setFee(f.key, { on: !on })} testid={`calc-fee-${f.key}`} />
                    <p className="text-[11px] text-sage mt-1 ml-8">
                      {on ? "Included" : "Unchecked: client to arrange / excluded"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-[0.14em] uppercase font-bold text-forest/60 mb-1">
                      {f.percent ? "Mortgage fee (%)" : "Amount (£)"}
                    </label>
                    <Input
                      type="number"
                      min="0"
                      step={f.percent ? "0.01" : "1"}
                      value={amtRaw ?? ""}
                      onChange={(e) => setFee(f.key, { amount: e.target.value })}
                      disabled={!on}
                      placeholder={f.percent ? "0.00" : "0"}
                      testid={`calc-fee-amount-${f.key}`}
                    />
                    {f.percent && on && amtNum > 0 && (
                      <p
                        data-testid={`calc-fee-broker-computed`}
                        className="text-[13px] font-semibold text-forest mt-2 tabular-nums"
                      >
                        {cash(computed)} ({amtNum}% of {cash(results.loan)})
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Panel>

      <Panel
        title="Compare deposits & income assumptions"
        subtitle="Uses your property price, interest rate and term. Required income is a gross household income illustration."
        testid="calc-compare-panel"
      >
        <div className="overflow-x-auto">
          <table className="min-w-[720px] w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">
                <th className="py-2 pr-3">Deposit</th>
                <th className="py-2 pr-3 text-right">Cash deposit</th>
                <th className="py-2 pr-3 text-right">Mortgage</th>
                <th className="py-2 pr-3 text-right">Income at 4.5×</th>
                <th className="py-2 pr-3 text-right">Income at 5×</th>
                <th className="py-2 pr-3 text-right">Income at 5.5×</th>
                <th className="py-2 pr-3 text-right">Repayment / month</th>
                <th className="py-2 text-right">Interest-only / month</th>
              </tr>
            </thead>
            <tbody>
              {[10, 15, 20, 25].map((d) => {
                const loan = results.v.price * (1 - d / 100);
                const rep = payment(loan, results.r, results.v.term);
                return (
                  <tr key={d} className="border-t border-forest/8">
                    <td className="py-2 pr-3">{d}%</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{cash((results.v.price * d) / 100)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{cash(loan)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{cash(loan / 4.5)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{cash(loan / 5)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{cash(loan / 5.5)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{cash(rep)}</td>
                    <td className="py-2 text-right tabular-nums">{cash((loan * results.r) / 12)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="Purchase cost summary" testid="calc-cash-panel">
        <Row label="Deposit" value={cash((results.v.price * results.v.deposit) / 100)} />
        <Row
          label="Broker fee"
          value={
            state.fees.broker?.on
              ? `${cash((results.loan * (Number(state.fees.broker.amount) || 0)) / 100)} (${state.fees.broker.amount || 0}% of ${cash(results.loan)})`
              : "Not selected"
          }
        />
        <Row label="Stamp Duty" value={cash(results.duty)} />
        <Row label="Upfront costs (selected fees)" value={cash(results.feeTotal)} />
        <div className="mt-4 bg-forest text-cream px-5 py-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-[11px] tracking-[0.16em] uppercase font-bold text-cream/80">
              Total cost (upfront cash)
            </p>
            <p className="font-serif text-2xl text-cream mt-1" data-testid="calc-cash-total">
              {cash(results.cashTotal)}
            </p>
          </div>
          <p className="text-[11px] text-cream/70 max-w-sm text-right leading-snug">
            Deposit + SDLT + selected fees. Unselected costs are excluded and must be budgeted separately.
          </p>
        </div>
      </Panel>

      <Panel testid="calc-export-panel">
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={downloadPdf}
            disabled={pdfBusy}
            data-testid="calc-pdf-download"
            className="inline-flex items-center gap-2 bg-forest hover:bg-forest-dark text-cream px-5 py-3 text-[11px] tracking-[0.2em] uppercase font-bold transition-colors disabled:opacity-50"
          >
            <FileDown size={14} /> {pdfBusy ? "Preparing…" : "Download PDF"}
          </button>
          <button
            type="button"
            onClick={() => printScenario(state, results, buyerLabel, joint)}
            data-testid="calc-pdf-print"
            className="inline-flex items-center gap-2 border border-forest/25 hover:border-forest text-forest px-5 py-3 text-[11px] tracking-[0.2em] uppercase font-bold transition-colors"
          >
            <Printer size={14} /> Print / Save PDF
          </button>
        </div>
        <p className="text-[12px] text-sage leading-relaxed">
          The PDF includes client details, address history, the full scenario breakdown, Stamp Duty, optional fees, the upfront cost summary and signature blocks.
        </p>
      </Panel>

      <Panel testid="calc-assumptions-panel">
        <details className="group">
          <summary className="font-serif text-xl text-forest cursor-pointer">
            Assumptions, tax rules & sources
          </summary>
          <div className="pt-4 space-y-3 text-[12.5px] text-ink/80 leading-relaxed">
            <p>PAYE salary estimates for England, Wales and Northern Ireland, tax year 2026/27. Scotland, self-employment, dividends, benefits in kind, non-standard tax codes and Marriage Allowance are excluded. Income Tax uses a £12,570 Personal Allowance, tapered by £1 for each £2 above £100,000, and 20% / 40% / 45% bands.</p>
            <p>Employee NI category A assumes an employee below State Pension age and equal monthly pay, with £1,048 / £4,189 monthly thresholds and 8% / 2% rates.</p>
            <p>The default 5.97% rate is an editable illustration, not a verified current average or mortgage offer.</p>
            <p>Income multiples are assumptions; lender affordability also depends on expenditure, credit profile, employment and property criteria. Interest-only payments do not reduce the mortgage balance — the full principal is due at term-end.</p>
            <p>SDLT applies to England and Northern Ireland only, individual buyers purchasing one ordinary dwelling. Wales uses LTT and Scotland uses LBTT.</p>
          </div>
        </details>
      </Panel>
    </SectionShell>
  );
};
